<template>
    <b-modal title="Speaker"
             size="lg"
             body-class="p-0"
             centered
             no-close-on-backdrop
             v-model="dialogOpen"
             @shown="onDialogShown"
             @hidden="onDialogHidden">
        <!--this binds the enter key to the insert event, i.e. makes the OK button the default button-->
        <vue-keypress key-event="keydown" :key-code="13" @success="handleEnterKey"></vue-keypress>

        <b-alert v-model="showErrors" variant="danger" dismissible>
            <div v-for="(error, index) in currentErrors" :key="index">
                <p>{{ error }}</p>
            </div>
        </b-alert>

        <b-list-group flush>
            <b-list-group-item v-if="(currentSpeakers == null) || (currentSpeakers.length == 0)">
                The list is currently empty
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center py-1"
                               v-for="(speaker, index) in currentSpeakers"
                               :key="index">
                <vue-keypress key-event="keydown"
                              :key-code="speaker.shortcut.keyCode"
                              :modifiers="speaker.shortcut.modifiers"
                              :preventDefault="true"
                              @success="insert(speaker)"
                              :key="'keypress' + speaker.shortcut.keyCode"
                              v-if="speaker.shortcut"></vue-keypress>
                <div class="p-0 m-0 flex-grow-1">
                    {{ speakerFullname(speaker) }}  <b-badge variant="info" class="ml-2 p-1"> {{ speaker.SpeakerType }}</b-badge>
                </div>

                <b-button-group size="sm">
                    <b-button variant="warning" class="text-nowrap" v-on:click="deleteSpeaker(speaker)">Delete</b-button>
                    <b-button variant="success" class="text-nowrap rounded-right" v-on:click="insert(speaker)">
                        {{ sectionId ? 'Select' : 'Insert' }}
                        <template v-if="speaker.shortcut">
                            &nbsp;[{{ speaker.shortcut.text }}]
                        </template>
                    </b-button>
                    <b-form-select size="sm" class="ml-1" :options="possibleShortcuts" @change="shortcutSelected(speaker, $event)"></b-form-select>
                </b-button-group>
            </b-list-group-item>
        </b-list-group>
        <hr class="mt-0" />
        <div ref="inputFields" class="m-3">
            <b-form-row>
                <b-form-group label="Speaker Group"
                              label-for="speakerGroup"
                              class="col-md-6">
                    <b-form-select ref="speakerGroup"
                                   name="speakerGroup"
                                   v-model="selectedSpeakerGroup"
                                   :options="speakerGroupOptions">
                    </b-form-select>
                </b-form-group>
                <b-form-group label="Speaker Type"
                              label-for="speakerType"
                              class="col-md-6">
                    <b-form-select ref="speakerType"
                                   name="speakerType"
                                   v-model="selectedSpeakerType"
                                   :options="speakerTypeOptions"
                                   :disabled="(speakerTypeOptions == null) || (speakerTypeOptions.length == 0)">
                    </b-form-select>
                </b-form-group>
                <b-form-group label="Title"
                              label-for="speakerTitle"
                              class="col-md-2">
                    <select2 name="speakerTitle"
                             v-model="selectedSpeakerTitle"
                             :options="titleOptions"
                             :disabled="(selectedSpeakerType == null) || (selectedSpeakerType.Title == 'disabled')"
                             tags
                             in-b-modal></select2>
                </b-form-group>
                <b-form-group label="Surname"
                              label-for="speakerSurname"
                              class="col-md-4">
                    <b-form-input name="speakerSurname"
                                  v-model="speakerSurname"
                                  :disabled="(selectedSpeakerType == null) || (selectedSpeakerType.Surname == 'disabled')">
                    </b-form-input>
                </b-form-group>
                <b-form-group label="Other Names"
                              label-for="speakerOtherNames"
                              class="col-md-4">
                    <b-form-input name="speakerOtherNames"
                                  v-model="speakerOtherNames"
                                  :disabled="(selectedSpeakerType == null) || (selectedSpeakerType.OtherName == 'disabled')">
                    </b-form-input>
                </b-form-group>

                <b-form-group label="Honorific Title"
                              label-for="honorificTitle"
                              class="col-md-2">
                    <select2 name="honorificTitle"
                             v-model="selectedHonorificTitle"
                             :options="honorificTitleOptions"
                             :disabled="(selectedSpeakerType == null) || (selectedSpeakerType.HonorificTitle == 'disabled')"
                             tags
                             in-b-modal></select2>
                </b-form-group>

            </b-form-row>
        </div>
        <template v-slot:modal-footer="{ cancel }">
            <b-button variant="danger" @click="reset">Reset</b-button>
            <b-button variant="primary"
                      :disabled="(speakerSurname==null || speakerSurname=='')"
                      @click="addToList">Add to List</b-button>
            <b-button @click="cancel">Cancel</b-button>
            <b-button variant="success"
                      :disabled="!(speakerSurname==null || speakerSurname=='')"
                      @click="saveAndExit">Save & Exit</b-button>
        </template>

    </b-modal>
</template>

<script>
    import $ from 'jquery';
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import VueKeypress from 'vue-keypress';
    import { v4 as newUuid } from 'uuid';
    import Select2 from '../Select2';

    import EventsMixin from '../../mixins/EventsMixin';

    Vue.use(BootstrapVue);

    export default {
        name: 'TopscribeSpeakerDialog',
        mixins: [EventsMixin],
        components: {
            'vue-keypress': VueKeypress,
            'select2': Select2
        },
        model: {
            event: 'change',
            prop: 'active',

        },
        props: {
            configuration: {
                type: Object,
                default: null
            },
            active: {
                type: Boolean,
                default: false
            },
            mode: {
                type: String,
                default: 'insert',
                validator: function (value) {
                    // The value must match one of these strings
                    return (['insert', 'edit'].indexOf(value) !== -1);
                }
            },
            currentEditorSection: {
                default: null
            }
        },
        data: function () {
            return {
                dialogOpen: this.active,
                currentConfiguration: null,
                configurationDirty: false,
                currentErrors: null,
                showErrors: false,
                
                selectedSpeakerGroup: null,
                selectedSpeakerType: null,
                selectedSpeakerTitle: null,
                selectedHonorificTitle: null,
                speakerSurname: null,
                speakerOtherNames: null,
                
                defaultPossibleShortcuts: [
                    { "keyCode": 65, "modifiers": ["altKey"], "text": "Alt+A", "value": "alt-a" },
                    { "keyCode": 66, "modifiers": ["altKey"], "text": "Alt+B", "value": "alt-b" },
                    { "keyCode": 67, "modifiers": ["altKey"], "text": "Alt+C", "value": "alt-c" },
                    { "keyCode": 68, "modifiers": ["altKey"], "text": "Alt+D", "value": "alt-d" },
                    { "keyCode": 69, "modifiers": ["altKey"], "text": "Alt+E", "value": "alt-e" },
                    { "keyCode": 70, "modifiers": ["altKey"], "text": "Alt+F", "value": "alt-f" },
                    { "keyCode": 71, "modifiers": ["altKey"], "text": "Alt+G", "value": "alt-g" },
                    { "keyCode": 72, "modifiers": ["altKey"], "text": "Alt+H", "value": "alt-h" },
                    { "keyCode": 73, "modifiers": ["altKey"], "text": "Alt+I", "value": "alt-i" },
                    { "keyCode": 74, "modifiers": ["altKey"], "text": "Alt+J", "value": "alt-j" },
                    { "keyCode": 75, "modifiers": ["altKey"], "text": "Alt+K", "value": "alt-k" },
                    { "keyCode": 76, "modifiers": ["altKey"], "text": "Alt+L", "value": "alt-l" },
                    { "keyCode": 77, "modifiers": ["altKey"], "text": "Alt+M", "value": "alt-m" },
                    { "keyCode": 78, "modifiers": ["altKey"], "text": "Alt+N", "value": "alt-n" },
                    { "keyCode": 79, "modifiers": ["altKey"], "text": "Alt+O", "value": "alt-o" },
                    { "keyCode": 80, "modifiers": ["altKey"], "text": "Alt+P", "value": "alt-p" },
                    { "keyCode": 81, "modifiers": ["altKey"], "text": "Alt+Q", "value": "alt-q" },
                    { "keyCode": 82, "modifiers": ["altKey"], "text": "Alt+R", "value": "alt-r" },
                    { "keyCode": 83, "modifiers": ["altKey"], "text": "Alt+S", "value": "alt-s" },
                    { "keyCode": 84, "modifiers": ["altKey"], "text": "Alt+T", "value": "alt-t" },
                    { "keyCode": 85, "modifiers": ["altKey"], "text": "Alt+U", "value": "alt-u" },
                    { "keyCode": 86, "modifiers": ["altKey"], "text": "Alt+V", "value": "alt-v" },
                    { "keyCode": 87, "modifiers": ["altKey"], "text": "Alt+W", "value": "alt-w" },
                    { "keyCode": 88, "modifiers": ["altKey"], "text": "Alt+X", "value": "alt-x" },
                    { "keyCode": 89, "modifiers": ["altKey"], "text": "Alt+Y", "value": "alt-y" },
                    { "keyCode": 90, "modifiers": ["altKey"], "text": "Alt+Z", "value": "alt-z" }
                ],
                sectionId: null,
                sectionsInsertedEventData: null,
                sectionUpdated: false
            }
        },
        computed: {
            titleOptions: function () {
                if (this.currentConfiguration == null) return null;
                //we would now usually return the list of honorific Titles (just a list of strings) from the configuration - loaded from the API, but as this is a sample i will generate them as a static array

                // return ['QC', 'SC', 'MP'];

                var options = [
                    { text: 'n/a', value: '' },
                    { text: 'MR', value: 'MR' },
                    { text: 'MS', value: 'MS' },
                    { text: 'DR', value: 'DR' },
                    { text: 'PROF', value: 'PROF' }
                ];
                return options;

            },

            honorificTitleOptions: function () {
                if (this.currentConfiguration == null) return null;
                //we would now usually return the list of honorific Titles (just a list of strings) from the configuration - loaded from the API, 
                // but as this is a sample i will generate them as a static array

                var options = [
                    { text: 'n/a', value: '' },
                    { text: 'DCJ', value: 'DCJ' },
                    { text: 'QC', value: 'QC' },
                    { text: 'SC', value: 'SC' },
                    { text: 'MP', value: 'MP' },
                    { text: 'KC', value: 'KC' }
                ];
                return options;

            },

            currentSpeakers: function () {
                if (this.currentConfiguration == null)
                    return null;
                //we have a list of speakers, which is the same for all turns related to a transcript
                //and then we might have a list of custom shortcuts which are specific to each user
                //now we go through the list of speakers and if the user has configured a custom shortcut, then that will be used,
                //otherwise we use the next possible shortcut that is not used as a custom shortcut
                //thus the steps are
                //1 work out the list of custom shortcuts (we store the list of the shortcut value property, e.g. alt-a)
                var usedCustomShortcuts = [];
                this.currentConfiguration.CustomShortcuts.forEach((customShortcut) => {
                    usedCustomShortcuts.push(customShortcut.Shortcut);
                });
                //2 now run through the list of speakers
                var speakers = [];
                var currentShortcut = 0;
                this.currentConfiguration.Speakers.forEach((speaker) => {
                    var shortcut = null;
                    if (this.currentConfiguration.CustomShortcuts) {
                        //3 check if there is a custom shortcut assigned to that speaker (custom shortcuts are stored with the SpeakerId and the value - e.g. alt-a - of the shortcut to use)
                        var customShortcut = this.currentConfiguration.CustomShortcuts.find((entry) => {
                            return entry.SpeakerId == speaker.SpeakerId;
                        });
                        if (customShortcut) {
                            shortcut = this.possibleShortcuts.find((entry) => {
                                return entry.value == customShortcut.Shortcut;
                            });
                        }
                    }
                    if (shortcut == null) {
                        //4 if no custom shortcut has been found, then take the next possible shortcut, as long as it is not in use as a custom shortcut
                        while (currentShortcut < this.possibleShortcuts.length) {
                            if (usedCustomShortcuts.indexOf(this.possibleShortcuts[currentShortcut].value) < 0) {
                                shortcut = this.possibleShortcuts[currentShortcut];
                            }
                            currentShortcut++;
                            if (shortcut != null) break;
                        }
                    }
                    speaker.shortcut = shortcut;
                    speakers.push(speaker);
                })
                return speakers;
            },
            possibleShortcuts: function () {
                if ((this.currentConfiguration == null) ||
                    (this.currentConfiguration.PossibleShortcuts == null)) {
                    return this.defaultPossibleShortcuts;
                }
                return this.currentConfiguration.PossibleShortcuts;
            },
            speakerGroupOptions: function () {
                if (this.currentConfiguration == null)
                    return null;
                return this.currentConfiguration.SpeakerGroupOptions;
            },
            speakerTypeOptions: function () {
                if (this.selectedSpeakerGroup == null)
                    return null;
                return this.selectedSpeakerGroup.SpeakerTypeOptions;
            }
        },
        watch: {
            configuration: function () {
                if ((this.configuration == null) || (this.configuration.SpeakerGroupOptions == null)) {
                    this.currentConfiguration = null;
                } else if (this.configuration.Speakers == null) {
                    this.currentConfiguration = {
                        SpeakerGroupOptions: this.configuration.SpeakerGroupOptions,
                        PossibleShortcuts: this.configuration.PossibleShortcuts ? this.configuration.PossibleShortcuts : null,
                        Speakers: [],
                        CustomShortcuts: [],

                    }
                } else {
                    this.currentConfiguration = {
                        SpeakerGroupOptions: this.configuration.SpeakerGroupOptions,
                        PossibleShortcuts: this.configuration.PossibleShortcuts ? this.configuration.PossibleShortcuts : null,
                        Speakers: this.configuration.Speakers,
                        CustomShortcuts: this.configuration.CustomShortcuts ? this.configuration.CustomShortcuts : []
                    }
                }
            },
            active: function () {
                if (this.active) {
                    //the dialog is about to open
                    this.currentErrors = null;
                    if (this.mode == 'edit') {
                        try {
                            var base64EncodedMetaData = $(this.currentEditorSection).data('metadata');
                            var metaData = JSON.parse(atob(base64EncodedMetaData));
                            this.sectionId = metaData.SectionId;
                        }
                        catch
                        {
                            console.warn('invalid metadata in section');
                        }
                    } else {
                        this.sectionId = null;
                    }
                    this.configurationDirty = false;
                    this.sectionsInsertedEventData = null;
                    this.sectionUpdated = false;
                }
                this.dialogOpen = this.active;
            },
            selectedSpeakerGroup: function () {
                if ((this.selectedSpeakerGroup != null) &&
                    (this.selectedSpeakerGroup.SpeakerTypeOptions != null) &&
                    (this.selectedSpeakerGroup.SpeakerTypeOptions.length == 1)) {
                    this.selectedSpeakerType = this.selectedSpeakerGroup.SpeakerTypeOptions[0].value;
                } else {
                    this.selectedSpeakerType = null;
                }
            },
            currentErrors: function () {
                this.showErrors = (this.currentErrors != null) && (this.currentErrors.length > 0);
            }
        },
        methods: {
            speakerFullname: function (speaker) {
                var fullName = '';
                if (speaker.Title != null) {
                    fullName += speaker.Title;
                }
                if (speaker.Surname != null) {
                    if (fullName.length > 0) {
                        fullName += " ";
                    }
                    fullName += speaker.Surname;
                }
                if (speaker.OtherNames != null) {
                    if (fullName.length > 0) {
                        fullName += " ";
                    }
                    fullName += speaker.OtherNames;
                }
                if (speaker.HonorificTitle != null) {
                    if (fullName.length > 0) {
                        fullName += " ";
                    }
                    fullName += speaker.HonorificTitle;
                }
                return fullName;
            },
            handleEnterKey: function (e) {
                if (this.isEventTargetWithinElement(e.event, this.$refs.inputFields)) {
                    this.addToList();
                }
            },
            reset: function () {
                if ((this.currentConfiguration != null) &&
                    (this.currentConfiguration.SpeakerGroupOptions != null) &&
                    (this.currentConfiguration.SpeakerGroupOptions.length == 1)) {
                    this.selectedSpeakerGroup = this.currentConfiguration.SpeakerGroupOptions[0].value;
                    if (this.$refs.speakerType) this.$refs.speakerType.$el.focus();
                } else {
                    this.selectedSpeakerGroup = null;
                    if (this.$refs.speakerGroup) this.$refs.speakerGroup.$el.focus();
                }
                this.selectedSpeakerTitle = null;
                this.speakerSurname = null;
                this.speakerOtherNames = null;
                this.selectedHonorificTitle = '';
                this.currentErrors = null;
            },
            addToList: function () {
                this.currentErrors = [];
                if (this.selectedSpeakerGroup == null) {
                    this.currentErrors.push('A speaker group has to be selected') 
                }
                if (this.selectedSpeakerType == null) {
                    this.currentErrors.push('A speaker type has to be selected')
                }
                if (this.selectedSpeakerType != null && this.selectedSpeakerType.Title == 'required' && this.selectedSpeakerTitle == null) {
                    this.currentErrors.push('A TITLE has to be selected !')
                }
                if (this.selectedSpeakerType != null && this.selectedSpeakerType.Surname == 'required' && (this.speakerSurname == null || this.speakerSurname.length == 0)) {
                    this.currentErrors.push('A speaker SURNAME is required !')
                }
                if (this.selectedSpeakerType != null && this.selectedSpeakerType.OtherName == 'required' && (this.speakerOtherNames == null || this.speakerOtherNames.length == 0)) {
                    this.currentErrors.push('OTHER NAMES  are required !')
                }
                if (this.selectedSpeakerType != null && this.selectedSpeakerType.HonorificTitle == 'required' && this.selectedHonorificTitle == null) {
                    this.currentErrors.push('A HONORIFIC TITLE has to be selected/entered !')
                }
                if (this.currentErrors.length == 0) { 
                    var speakerItem = {
                        SpeakerId: newUuid()
                    };
                    speakerItem.SpeakerType = this.selectedSpeakerType.SpeakerType;
                    speakerItem.Title = this.selectedSpeakerTitle;
                    speakerItem.Surname = this.speakerSurname;
                    speakerItem.OtherNames = this.speakerOtherNames;
                    speakerItem.HonorificTitle = this.selectedHonorificTitle;

                    this.currentConfiguration.Speakers.push(speakerItem);
                    this.configurationDirty = true;
                    this.reset();
                }
                
            },
            deleteSpeaker: function (speaker) {
                var indexToRemove = this.currentConfiguration.Speakers.indexOf(speaker);
                if (indexToRemove >= 0) {
                    this.currentConfiguration.Speakers.splice(indexToRemove, 1);
                    if (this.currentConfiguration.CustomShortcuts != null) {
                        indexToRemove = this.currentConfiguration.CustomShortcuts.findIndex((entry) => {
                            return entry.SpeakerId == speaker.SpeakerId;
                        });
                        if (indexToRemove >= 0) {
                            this.currentConfiguration.CustomShortcuts.splice(indexToRemove, 1);
                        }
                    }
                    this.configurationDirty = true;
                }
            },
            shortcutSelected: function (speaker, value) {
                if (!this.currentConfiguration.CustomShortcuts) this.currentConfiguration.CustomShortcuts = [];
                var customShortcut = this.currentConfiguration.CustomShortcuts.find((entry) => {
                    return entry.SpeakerId == speaker.SpeakerId;
                });
                if (customShortcut) customShortcut.Shortcut = value;
                else this.currentConfiguration.CustomShortcuts.push({ SpeakerId: speaker.SpeakerId, Shortcut: value });
                var collidingEntryIndex  = this.currentConfiguration.CustomShortcuts.findIndex((entry) => {
                    return (entry.Shortcut == value) && (entry.SpeakerId != speaker.SpeakerId);
                });
                if (collidingEntryIndex > -1) this.currentConfiguration.CustomShortcuts.splice(collidingEntryIndex, 1);
                this.configurationDirty = true;
            },
            insert: function (speaker) {
                this.currentErrors = []

                if (this.currentErrors.length == 0) {
                    if (!this.sectionId) this.sectionId = newUuid();
                    var metaData = {
                        SectionId: this.sectionId,
                        Type: 'speaker',
                        SubType: speaker.SpeakerType,
                        SpeakerId: speaker.SpeakerId,
                        Title: speaker.Title,
                        Surname: speaker.Surname,
                        OtherNames: speaker.OtherNames,
                        HonorificTitle: speaker.HonorificTitle
                    }
                    var base64EncodedMetaData = btoa(JSON.stringify(metaData));

                    var marginText = '';
                    if (speaker.Title) marginText += speaker.Title.trim();
                    if (speaker.OtherNames) {
                        if (marginText.length > 0) marginText += ' ';
                        marginText += speaker.OtherNames.trim();
                    }
                    if (speaker.Surname) {
                        if (marginText.length > 0) marginText += ' ';
                        marginText += speaker.Surname.trim();
                    }
                    if (speaker.HonorificTitle) {
                        if (marginText.length > 0) marginText += ' ';
                        marginText += speaker.HonorificTitle.trim();
                    }
                    marginText += ':';

                    var speakerSection = null;
                    this.$emit('beforeSectionsInsertOrUpdate');
                    Vue.nextTick(() => {
                        if (this.mode == 'edit') {
                            speakerSection = $(this.currentEditorSection);
                            speakerSection
                                .attr('data-sub-type', speaker.SpeakerType)
                                .data('sub-type', speaker.SpeakerType) //IMPORTANT! we need to update both, the data-* attribute and the .data entry, because one is the actual HTML that is read by the server and one is the data that jquery stores internally
                                .attr('data-metadata', base64EncodedMetaData)
                                .data('metadata', base64EncodedMetaData) //IMPORTANT! we need to update both, the data-* attribute and the .data entry, because one is the actual HTML that is read by the server and one is the data that jquery stores internally
                                .removeAttr('data-margintext')
                                .removeData('margintext'); //IMPORTANT! we need to remove both, the data-* attribute and the .data entry, because one is the actual HTML that is read by the server and one is the data that jquery stores internally
                            $('.speaker-name', speakerSection).remove(); //remove the old margin text, a new one will be inserted later
                            this.sectionUpdated = true;
                        } else {
                            speakerSection =
                                $('<div data-type="speaker"/>')
                                    .attr('id', metaData.SectionId)
                                    .attr('data-sub-type', speaker.SpeakerType)
                                    .attr('data-metadata', base64EncodedMetaData)
                                    .insertAfter(this.currentEditorSection);
                            var speakerSubSection =
                                $('<div data-type="speaker-subsection" data-sub-type="statement"/>')
                                    .attr('id', newUuid())
                                    .appendTo(speakerSection);
                            var insertPosition = $('<p><br/></p>').appendTo(speakerSubSection)[0];
                            this.sectionsInsertedEventData = { sections: [speakerSection[0]], insertPosition: insertPosition };
                        }
                        if (marginText != null) {
                            speakerSection
                                .attr('data-margintext', marginText)
                                .data('margintext', marginText);
                        }
                        if (this.configurationDirty) {
                            this.saveAndExit();
                        } else {
                            this.dialogOpen = false;
                        }
                    });
                }
            },
            saveAndExit: function () {
                this.$emit('configurationChanged', { dialogName: "speaker", configuration: this.currentConfiguration });
                this.dialogOpen = false;
            },
            onDialogShown: function () {
                this.reset();
            },
            onDialogHidden: function () {
                this.reset();
                this.$emit('change', false);
                if (this.sectionsInsertedEventData) {
                    this.$emit('sectionsInserted', this.sectionsInsertedEventData);
                } else if (this.sectionUpdated) {
                    this.$emit('sectionUpdated');
                } else {
                    this.$emit('cancelled');
                }
                this.sectionsInsertedEventData = null;
                this.sectionUpdated = false;
            }
        }
    }
</script>
